import { useMemo, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Box from 'ui/src/components/Box'
import Typography from 'ui/src/components/Typography'
import theme from 'ui/src/common/theme'
import Avatar from 'ui/src/components/Avatar'
import { Tooltip } from 'ui/src/components/Tooltip'
import { IRegister } from 'models/register.model'
import PersonalAccount from './components/PersonalAccount'

interface ProfileMenuProps {
  avatar: string
  userAvatar: string
  companyAvatar: string
  handleSignOut: () => void
  isProfileMenuOpen: boolean
  hasHighLevelPrivileges?: boolean
  setIsProfileMenuOpen: (isProfileMenuOpen: boolean) => void
  profileSelected: 'individual' | 'company'
  register?: IRegister | undefined
}

const ProfileMenu = ({
  avatar,
  userAvatar,
  companyAvatar,
  handleSignOut,
  isProfileMenuOpen,
  hasHighLevelPrivileges = false,
  setIsProfileMenuOpen,
  profileSelected,
  register,
}: ProfileMenuProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)

  const handleModalClose = useCallback(() => {
    setIsProfileMenuOpen(false)
  }, [setIsProfileMenuOpen])

  const renderProfileMenu = useMemo(() => {
    return (
      <Box
        ref={ref}
        sx={{
          backgroundColor: 'gray.0',
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          visibility: isProfileMenuOpen ? 'visible' : 'hidden',
          borderRadius: '0.25rem',
          justifyContent: 'center',
          boxShadow: theme.shadows[2],
          alignItems: 'flex-start',
          left: '-0.938rem',
          top: '3.125rem',
          width: '11.5rem',
          height: '8.313rem',
          padding: '1rem',
          gap: '1rem',
          zIndex: 10,
        }}
      >
        {hasHighLevelPrivileges && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
              gap: '0.75rem',
              cursor: 'pointer',
            }}
          >
            <Avatar sx={{ width: '1.5rem', height: '1.5rem' }} src={companyAvatar} />
            <Typography
              onClick={() => {
                navigate('/company/dashboard')
                handleModalClose()
              }}
              variant={profileSelected === 'company' ? 'subtitle1' : 'body3'}
            >
              {t('default.profileMenu.adminAccount')}
            </Typography>
          </Box>
        )}
        <PersonalAccount
          register={register}
          userAvatar={userAvatar}
          isProfileMenuOpen={isProfileMenuOpen}
          setIsProfileMenuOpen={setIsProfileMenuOpen}
          profileSelected={profileSelected}
        />
        <Typography
          onClick={handleSignOut}
          variant="body3"
          sx={{
            color: 'feedback.negative.500',
            cursor: 'pointer',
          }}
        >
          {t('default.profileMenu.logout')}
        </Typography>
      </Box>
    )
  }, [
    companyAvatar,
    handleSignOut,
    isProfileMenuOpen,
    navigate,
    userAvatar,
    hasHighLevelPrivileges,
    profileSelected,
    t,
    handleModalClose,
    register,
    setIsProfileMenuOpen,
  ])

  return (
    <>
      <Box
        sx={{
          position: 'relative',
        }}
      >
        <Tooltip
          title="Profile"
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 7],
                  },
                },
              ],
            },
          }}
        >
          <Avatar
            data-cy="avatar"
            sx={{
              cursor: 'pointer',
              width: '1.625rem',
              height: '1.625rem',
              outline: '0.125rem solid #00B5CB',
              border: '0.063rem solid',
              borderColor: 'gray.0',
            }}
            src={avatar}
            onClick={() => {
              setIsProfileMenuOpen(!isProfileMenuOpen)
            }}
          />
        </Tooltip>
        {renderProfileMenu}
      </Box>
    </>
  )
}

export default ProfileMenu
